import { useMemo, useState, type CSSProperties } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Send, UserCheck, X, Mail, Bot, Link2, UserX, Paperclip, Download, ShieldCheck, ShieldAlert } from 'lucide-react';
import {
  listConversations, fetchConversation,
  approveConversation, takeOverConversation, closeConversation, reopenConversation, draftConversation, linkConversationDriver, markConversationNotDriver,
  conversationAttachmentPath, getDisplayErrorMessage,
  type ConversationStatus, type ConversationListRow, type ConversationView, type ConversationDetail, type ConversationMessageView,
} from '@/lib/api';
import styles from './ConversationsPage.module.css';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import { useAuthedDownload } from '@/hooks/useAuthedDownload';

interface Props {
  onNavigate?: (to: string) => void;
}

const STATUS_LABEL: Record<ConversationStatus, string> = {
  needs_review: 'Needs review',
  human_handling: 'Human handling',
  awaiting_reply: 'Awaiting reply',
  closed: 'Closed',
};
const STATUS_COLOR: Record<ConversationStatus, string> = {
  needs_review: '#f39c12',
  human_handling: '#3498db',
  awaiting_reply: '#21c163',
  closed: 'var(--color-tx2)',
};
const FILTERS: Array<{ key: ConversationStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'needs_review', label: 'Needs review' },
  { key: 'human_handling', label: 'Human handling' },
  { key: 'awaiting_reply', label: 'Awaiting reply' },
  { key: 'closed', label: 'Closed' },
];
/** The views (29 October 2026): the inbox hides vendor and bulk mail, which waits under Filtered, never answered. */
const VIEWS: Array<{ key: ConversationView; label: string }> = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'support_mail', label: 'support@ mail' },
  { key: 'unmatched', label: 'No driver yet' },
  { key: 'filtered', label: 'Filtered' },
];
const MATCH_LABEL: Record<string, string> = {
  alias: 'reply alias', email: 'matched by email', reference: 'matched by our email', person_link: 'linked by a person',
  ambiguous: 'several accounts', candidates: 'possible matches', unmatched: 'no driver yet', not_driver: 'not a driver', staff: 'EVI staff',
};
const SEND_STATUS_LABEL: Record<string, string> = {
  sending: 'sending', not_sent: 'not sent', unknown: 'may not have gone', bounced: 'bounced',
};
const DOWNLOADABLE = /^(image\/(png|jpe?g|gif|webp|heic|heif)|application\/pdf)$/;

/** Where the thread came from, in a few words: the alias or the reply route. */
function sourceLabel(c: Pick<ConversationListRow, 'source' | 'channel'>): string {
  if (c.source === 'support_mail') {
    if (c.channel?.route === 'staff_forward') return 'forwarded by EVI';
    return `via ${c.channel?.group_alias ?? 'support'}@`;
  }
  return 'reply to our email';
}

function who(c: Pick<ConversationListRow, 'user' | 'match'>): string {
  return c.user?.name ?? c.user?.email ?? c.match?.sender_email ?? 'Unknown sender';
}

function authorLabel(m: ConversationMessageView, d: ConversationDetail): string {
  if (m.direction === 'inbound') return m.from_email ?? d.match?.sender_email ?? 'Driver';
  if (m.author === 'evii') return m.auto_reply ? 'EVII (automatic)' : 'EVII';
  if (m.author === 'person') return 'EVI person';
  return 'EVI (sent)';
}

/**
 * Conversations: driver email, both replies to our emails and every message
 * to support@ (29 October 2026, Warren's item 3). EVII drafts; a person
 * approves (send the draft or an edit), takes over, or closes the thread. A
 * thread with no driver yet shows the match evidence and a person links the
 * driver or marks it not a driver. The Api decides who may do what (Super
 * Admin and Operator; the link and the attachments are person only) and a
 * refusal shows as the error line.
 */
export function ConversationsPage({ onNavigate: _onNavigate }: Props = {}) {
  useRecentRoute('Conversations');
  const qc = useQueryClient();
  const dl = useAuthedDownload();
  const [statusFilter, setStatusFilter] = useState<ConversationStatus | 'all'>('needs_review');
  const [view, setView] = useState<ConversationView>('inbox');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draftEdit, setDraftEdit] = useState<string | null>(null);
  const [linkId, setLinkId] = useState('');
  const [actionErr, setActionErr] = useState('');

  const listQ = useQuery({
    queryKey: ['conversations', view, statusFilter],
    queryFn: () => listConversations({ view, ...(statusFilter === 'all' ? {} : { status: statusFilter }) }),
  });

  const detailQ = useQuery({
    queryKey: ['conversation', selectedId],
    queryFn: () => fetchConversation(selectedId!),
    enabled: Boolean(selectedId),
  });

  // Reset the editable draft whenever a new conversation loads.
  const detail = detailQ.data;
  const detailDraftBody = detail?.draft?.body ?? '';
  const effectiveDraft = draftEdit ?? detailDraftBody;

  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: ['conversations'] });
    void qc.invalidateQueries({ queryKey: ['conversation', selectedId] });
  };
  const done = (): void => { setDraftEdit(null); setActionErr(''); setLinkId(''); refresh(); };

  const approveM = useMutation({
    mutationFn: (body: string) => approveConversation(selectedId!, body || undefined),
    onSuccess: done,
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'Failed to send the reply.')),
  });
  const takeOverM = useMutation({
    mutationFn: () => takeOverConversation(selectedId!),
    onSuccess: done,
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'Failed to take over.')),
  });
  const closeM = useMutation({
    mutationFn: () => closeConversation(selectedId!),
    onSuccess: done,
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'Failed to close.')),
  });
  const reopenM = useMutation({
    mutationFn: () => reopenConversation(selectedId!),
    onSuccess: () => { setStatusFilter('all'); done(); },
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'Failed to reopen.')),
  });
  const draftM = useMutation({
    mutationFn: () => draftConversation(selectedId!),
    onSuccess: done,
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'EVII could not draft a reply.')),
  });
  const linkM = useMutation({
    mutationFn: (userId: string) => linkConversationDriver(selectedId!, userId),
    onSuccess: done,
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'Failed to link the driver.')),
  });
  const notDriverM = useMutation({
    mutationFn: () => markConversationNotDriver(selectedId!),
    onSuccess: done,
    onError: async (e) => setActionErr(await getDisplayErrorMessage(e, 'Failed to mark not a driver.')),
  });

  const rows = useMemo(() => listQ.data?.conversations ?? [], [listQ.data]);
  const busy = approveM.isPending || takeOverM.isPending || closeM.isPending || linkM.isPending || notDriverM.isPending || reopenM.isPending || draftM.isPending;
  const isClosed = detail?.status === 'closed';
  const unmatched = Boolean(detail && !detail.user);

  return (
    <div className={styles.page}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 4px' }}>Conversations</h1>
      <p style={{ color: 'var(--color-tx2)', fontSize: 13, margin: '0 0 16px' }}>
        Driver email: replies to our emails and every message to support@. EVII drafts a reply; approve it, edit and send, take over, or close.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        {VIEWS.map((v) => (
          <button key={v.key} type="button" onClick={() => { setView(v.key); setSelectedId(null); if (v.key === 'filtered') setStatusFilter('all'); }}
            style={chip(view === v.key, '#3498db')}>{v.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => { setStatusFilter(f.key); setSelectedId(null); }} style={chip(statusFilter === f.key, '#21c163')}>
            {f.label}
          </button>
        ))}
      </div>

      <div className={styles.columns}>
        {/* List */}
        <div style={{ border: '1px solid var(--color-brd)', borderRadius: 12, overflow: 'hidden', background: 'var(--color-sf)' }}>
          {listQ.isLoading ? (
            <div style={{ padding: 24, color: 'var(--color-tx2)' }}><Loader2 size={16} className="spin" /> Loading.</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, color: 'var(--color-tx2)', fontSize: 13 }}>No conversations.</div>
          ) : (
            rows.map((c: ConversationListRow) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { setSelectedId(c.id); setDraftEdit(null); setActionErr(''); setLinkId(''); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '12px 14px', cursor: 'pointer',
                  borderBottom: '1px solid var(--color-brd)',
                  background: selectedId === c.id ? 'var(--color-sf2)' : 'transparent', color: 'var(--color-tx)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{who(c)}</span>
                  <span style={{ fontSize: 11, color: STATUS_COLOR[c.status], flexShrink: 0 }}>{STATUS_LABEL[c.status]}</span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-tx2)', margin: '2px 0' }}>{c.subject}</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '2px 0' }}>
                  <span style={badge('#2a2a2a', '#bbb')}>{sourceLabel(c)}</span>
                  {c.channel?.human_only && <span style={badge('#3a2a10', '#f3c26b')}>for a person</span>}
                  {c.match && !c.user && <span style={badge('#2a1a1a', '#ff9c9c')}>{MATCH_LABEL[c.match.status] ?? c.match.status}</span>}
                  {c.filtered && <span style={badge('#222', 'var(--color-tx2)')}>filtered: {c.filtered.reason.replace(/_/g, ' ')}</span>}
                </div>
                {c.last_message && (
                  <div style={{ fontSize: 12, color: 'var(--color-tx2)', display: 'flex', gap: 6, alignItems: 'center' }}>
                    {c.last_message.direction === 'inbound' ? <Mail size={11} /> : <Send size={11} />}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.last_message.preview}</span>
                  </div>
                )}
                {c.has_draft && <span style={{ fontSize: 11, color: '#21c163', display: 'inline-flex', gap: 4, alignItems: 'center', marginTop: 2 }}><Bot size={11} /> draft ready</span>}
              </button>
            ))
          )}
        </div>

        {/* Detail */}
        <div style={{ border: '1px solid var(--color-brd)', borderRadius: 12, background: 'var(--color-sf)', minHeight: 300, padding: 16, minWidth: 0 }}>
          {!selectedId ? (
            <div style={{ color: 'var(--color-tx2)', fontSize: 13 }}>Select a conversation.</div>
          ) : detailQ.isLoading || !detail ? (
            <div style={{ color: 'var(--color-tx2)' }}><Loader2 size={16} className="spin" /> Loading.</div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{detail.user ? (detail.user.name ?? 'Driver') : detail.match?.status === 'staff' ? 'EVI staff sender' : 'No driver yet'}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-tx2)', wordBreak: 'break-all' }}>{detail.user?.email ?? detail.match?.sender_email ?? ''}</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                    <span style={badge('#2a2a2a', '#bbb')}>{sourceLabel(detail)}</span>
                    {detail.match && <span style={badge('#1f2633', '#9cc3ff')}>{MATCH_LABEL[detail.match.status] ?? detail.match.status}</span>}
                    {detail.channel?.human_only && <span style={badge('#3a2a10', '#f3c26b')}>for a person{detail.channel.human_reason ? `: ${detail.channel.human_reason.replace(/_/g, ' ')}` : ''}</span>}
                    {detail.match?.multiple_accounts && <span style={badge('#3a2a10', '#f3c26b')}>several accounts</span>}
                    {detail.filtered && <span style={badge('#222', 'var(--color-tx2)')}>filtered: {detail.filtered.reason.replace(/_/g, ' ')}</span>}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: STATUS_COLOR[detail.status] }}>{STATUS_LABEL[detail.status]}</span>
              </div>

              {/* Match evidence: who this sender may be, for a person to link. */}
              {detail.match_detail && (unmatched || detail.match_detail.unmatched_references.length > 0) && (
                <div style={{ border: '1px solid var(--color-brd)', borderRadius: 10, padding: 10, marginBottom: 12, fontSize: 12.5 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Who this may be</div>
                  {detail.match_detail.candidates.length === 0 && <div style={{ color: 'var(--color-tx2)' }}>{detail.match?.status === 'staff' ? 'This address is recognized as EVI staff. It is kept separate from driver accounts; automatic replies are paused.' : 'No driver account or session matches the details in this mail.'}</div>}
                  {detail.match_detail.candidates.map((c, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', padding: '6px 0', borderTop: i ? '1px solid #1c1c1c' : 'none', flexWrap: 'wrap' }}>
                      <div style={{ minWidth: 0 }}>
                        <div>{c.user ? `${c.user.name ?? 'Driver'} (${c.user.email ?? 'no email'})` : 'Guest session (no account)'}</div>
                        {c.session && (
                          <div style={{ color: 'var(--color-tx2)' }}>
                            {c.session.started_at ? new Date(c.session.started_at).toLocaleString() : 'date unknown'}
                            {c.session.location ? `, ${c.session.location}` : ''}
                            {c.session.total_cents !== null ? `, ${(c.session.total_cents / 100).toFixed(2)} ${c.session.currency ?? ''}` : ''}
                          </div>
                        )}
                        <div style={{ color: 'var(--color-tx2)' }}>matched on {c.evidence.map((e) => e.replace(/_/g, ' ')).join(', ')}</div>
                      </div>
                      {c.user && unmatched && !isClosed && (
                        <button type="button" disabled={busy} onClick={() => linkM.mutate(c.user!.id)} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                          <Link2 size={13} /> Link this driver
                        </button>
                      )}
                    </div>
                  ))}
                  {detail.match_detail.unmatched_references.length > 0 && (
                    <div style={{ color: 'var(--color-tx2)', marginTop: 6 }}>
                      References we cannot match on: {detail.match_detail.unmatched_references.join(', ')}
                    </div>
                  )}
                  {unmatched && !isClosed && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                      <input value={linkId} onChange={(e) => setLinkId(e.target.value.trim())} placeholder="Driver id (24 characters)" maxLength={24}
                        style={{ flex: '1 1 200px', background: '#0c0c0c', color: 'var(--color-tx)', border: '1px solid var(--color-brd)', borderRadius: 8, padding: '6px 10px', fontSize: 12.5 }} />
                      <button type="button" disabled={busy || !/^[a-f0-9]{24}$/i.test(linkId)} onClick={() => linkM.mutate(linkId)} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                        <Link2 size={13} /> Link driver
                      </button>
                      {detail.match?.status !== 'not_driver' && (
                        <button type="button" disabled={busy} onClick={() => notDriverM.mutate()} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                          <UserX size={13} /> Not a driver
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Thread */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                {detail.messages.map((m, i) => (
                  <div
                    key={i}
                    style={{
                      alignSelf: m.direction === 'inbound' ? 'flex-start' : 'flex-end',
                      maxWidth: '85%', padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                      background: m.direction === 'inbound' ? 'var(--color-sf2)' : 'var(--color-accent-soft)', color: 'var(--color-tx)',
                    }}
                  >
                    <div style={{ fontSize: 10.5, color: 'var(--color-tx2)', marginBottom: 2, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span>{authorLabel(m, detail)}</span>
                      {m.direction === 'inbound' && m.hop_verdict === 'pass' && <span title="Came through the support@ group"><ShieldCheck size={11} /> via group</span>}
                      {m.direction === 'inbound' && m.sender_verdict && m.sender_verdict !== 'unknown' && (
                        <span title="Whether the sender address is proven">
                          {m.sender_verdict === 'verified' ? <ShieldCheck size={11} /> : <ShieldAlert size={11} />} sender {m.sender_verdict.replace(/_/g, ' ')}
                        </span>
                      )}
                      {m.send_status && SEND_STATUS_LABEL[m.send_status] && <span style={{ color: '#ff9c9c' }}>{SEND_STATUS_LABEL[m.send_status]}</span>}
                    </div>
                    {m.body}
                    {(m.attachments ?? []).length > 0 && (
                      <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {(m.attachments ?? []).map((a) => (
                          <div key={a.k} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, color: '#bbb', flexWrap: 'wrap' }}>
                            <Paperclip size={12} /> {a.filename} ({Math.max(1, Math.round(a.size / 1024))} KB)
                            {DOWNLOADABLE.test(a.content_type) && m.n !== undefined ? (
                              <button type="button" disabled={dl.isBusy(`${m.n}:${a.k}`)} style={btn('var(--color-sf2)', 'var(--color-tx)')}
                                onClick={() => void dl.download(`${m.n}:${a.k}`, conversationAttachmentPath(detail.id, m.n!, a.k), a.filename)}>
                                <Download size={12} /> Download
                              </button>
                            ) : <span style={{ color: 'var(--color-tx2)' }}>not downloadable here</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {dl.error && <div role="alert" style={{ color: '#ff6b6b', fontSize: 12, marginBottom: 8 }}>{dl.error}</div>}

              {detail.handoff && (
                <div style={{ fontSize: 12, color: 'var(--color-tx2)', marginBottom: 10 }}>
                  Handed to a person{detail.handoff.reason ? `: ${detail.handoff.reason}` : '.'}
                </div>
              )}

              <p className={styles.sender}>From: EV Initiative Support &lt;support@evinitiative.com&gt;. EVII drafts for review; nothing sends until you approve.</p>
              {!detail.draft && !isClosed && <p className={styles.sender}>{detail.channel?.human_only
                ? 'This thread needs a person. You can request a draft to review or write the reply yourself.'
                : 'No draft is saved yet. Ask EVII to draft a reply or write one below.'}</p>}
              {/* Draft + actions */}
              {!isClosed && (
                <div>
                  <label style={{ fontSize: 12, color: 'var(--color-tx2)', display: 'block', marginBottom: 4 }}>
                    {detail.draft ? 'EVII draft (edit before sending):' : unmatched ? 'Write a reply (no account details: no driver is linked):' : 'Write a reply:'}
                  </label>
                  <textarea
                    aria-label="Email reply"
                    value={effectiveDraft}
                    onChange={(e) => setDraftEdit(e.target.value)}
                    rows={6}
                    style={{
                      width: '100%', background: '#fff', color: '#171717', border: '1px solid #767676',
                      borderRadius: 8, padding: 10, fontSize: 13, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box',
                    }}
                    placeholder="Type a reply, or edit EVII's draft."
                  />
                  {actionErr && <div role="alert" style={{ color: '#ff6b6b', fontSize: 12, marginTop: 6 }}>{actionErr}</div>}
                  <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <button type="button" disabled={busy || Boolean(draftEdit?.trim())} onClick={() => draftM.mutate()} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                      {draftM.isPending ? <Loader2 size={13} className="spin" /> : <Bot size={13} />} Ask EVII for a draft
                    </button>
                    <button
                      type="button"
                      disabled={busy || !effectiveDraft.trim()}
                      onClick={() => approveM.mutate(effectiveDraft.trim())}
                      style={btn('#21c163', '#000')}
                    >
                      {approveM.isPending ? <Loader2 size={13} className="spin" /> : <Send size={13} />} Approve &amp; send
                    </button>
                    {detail.status !== 'human_handling' && (
                      <button type="button" disabled={busy} onClick={() => takeOverM.mutate()} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                        {takeOverM.isPending ? <Loader2 size={13} className="spin" /> : <UserCheck size={13} />} Take over
                      </button>
                    )}
                    <button type="button" disabled={busy} onClick={() => closeM.mutate()} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                      {closeM.isPending ? <Loader2 size={13} className="spin" /> : <X size={13} />} Close
                    </button>
                  </div>
                </div>
              )}
              {isClosed && <div className={styles.sender}>This conversation is closed.
                <button type="button" disabled={busy} onClick={() => reopenM.mutate()} style={btn('var(--color-sf2)', 'var(--color-tx)')}>
                  {reopenM.isPending && <Loader2 size={13} className="spin" />} Reopen conversation
                </button>
              </div>}
              {isClosed && actionErr && <div role="alert" style={{ color: '#ff6b6b', fontSize: 12, marginTop: 6 }}>{actionErr}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function btn(bg: string, fg: string): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8,
    border: '1px solid var(--color-brd)', background: bg, color: fg, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  };
}

function chip(on: boolean, color: string): CSSProperties {
  return {
    padding: '6px 12px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer', border: '1px solid var(--color-brd)',
    background: on ? color : 'var(--color-sf2)', color: on ? '#000' : 'var(--color-tx)',
  };
}

function badge(bg: string, fg: string): CSSProperties {
  return { fontSize: 10.5, padding: '1px 7px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' };
}
